import { Component, OnDestroy, OnInit } from '@angular/core';
import { TreeNode } from './tree-node.model';
import { TreeNode as PrimeNgTreeNode } from 'primeng/api';
import { TreeService } from './tree.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '../alert/alert.service';
import { ModalConfirmComponent } from '../modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TREE_STATE_PERSISTED } from '../../app.events';
import { JhiEventManager } from '../core-utils';
import { SubscriptionsManager } from '../util/subscriptions-manager';

export type SelectionMode = 'single' | 'multiple';

@Component({
    template: '',
})
export abstract class TreeComponent implements OnInit, OnDestroy {

    private readonly CROP_LIST_FOLDER = 'CROPLISTS';
    private readonly PROGRAM_LIST_FOLDER = 'LISTS';
    private FORBIDDEN_CHARS = ['<', '>', '?', '%', '|'];
    readonly NAME_MAX_LENGTH: number = 50;

    mode: Mode = Mode.None;
    Modes = Mode;
    isReadOnly = false;
    isFolderSelectionMode = false;
    rootFolderLabels?: string[];

    public nodes: PrimeNgTreeNode[] = [];
    name: string;
    // primeng sets either one or the depending on the mode
    _selectedNodes: PrimeNgTreeNode[] | PrimeNgTreeNode;
    public selectionMode: SelectionMode;

    disabledAddActionMessage: string;
    disabledRenameActionMessage: string;
    disabledDeleteActionMessage: string;
    showAddToParentList = false;

    subscriptionManager = new SubscriptionsManager();

    private draggedNode: PrimeNgTreeNode;

    get selectedNodes(): PrimeNgTreeNode[] {
        if (!this._selectedNodes) {
            return [];
        }
        if (this.selectionMode === 'multiple' && Array.isArray(this._selectedNodes)) {
            return this._selectedNodes as PrimeNgTreeNode[];
        }
        return [this._selectedNodes as PrimeNgTreeNode];
    }

    constructor(public service: TreeService,
                public activeModal: NgbActiveModal,
                public alertService: AlertService,
                public translateService: TranslateService,
                public modalService: NgbModal,
                public eventManager?: JhiEventManager) {
        this.disabledAddActionMessage = '';
        this.disabledRenameActionMessage = '';
        this.disabledDeleteActionMessage = '';
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.subscriptionManager.add = this.service.init()
            .subscribe((nodes: TreeNode[]) => {
                nodes.forEach((node) => this.addNode(node));
                this.nodes.forEach((rootNode) => {
                    const node = nodes.find((c) => rootNode.data.id === c.key);
                    if (node && node.children) {
                        this.addChildren(rootNode, node.children);
                    }
                });

                this.redrawNodes();
            });
    }

    addNode(node: TreeNode) {
        if (this.isFolderSelectionMode && !node.isFolder) {
            return;
        }
        return this.nodes.push(this.toPrimeNgNode(node));
    }

    onNodeExpand(event) {
        if (event.node) {
            this.expand(event.node);
        }
    }

    onNodeSelect(event) {
        const node: PrimeNgTreeNode = event.node;
        if (!node.expanded) {
            this.expand(node);
        }
    }

    finish(extraParams?: any) {
        const persistPromise = this.persistTreeState();
        persistPromise.then(() => {
            const selected: TreeComponentResult[] = this.selectedNodes.filter((node: PrimeNgTreeNode) => {
                const isFolder = !node.leaf;
                return this.isFolderSelectionMode ? isFolder : !isFolder;
            }).map((node: PrimeNgTreeNode) => {
                return <TreeComponentResult>({
                    id: node.data.id,
                    name: node.data.name,
                    extraParams
                });
            });
            this.activeModal.close(selected);
        });
    }

    addListToParents(isAddToFemaleParents: boolean, extraParams?: any) {
        const persistPromise = this.persistTreeState();
        persistPromise.then(() => {
            const selected: TreeComponentResult[] = this.selectedNodes.filter((node: PrimeNgTreeNode) => {
                const isFolder = !node.leaf;
                return this.isFolderSelectionMode ? isFolder : !isFolder;
            }).map((node: PrimeNgTreeNode) => {
                return <TreeComponentResult>({
                    id: node.data.id,
                    name: node.data.name,
                    extraParams
                });
            });
            if (isAddToFemaleParents) {
                this.eventManager.broadcast({ name: 'addToFemaleParents', content: selected });
            } else {
                this.eventManager.broadcast({ name: 'addToMaleParents', content: selected });
            }
            this.activeModal.close();
        });
    }

    onDragStart(event, node: PrimeNgTreeNode) {
        this.setSelectedNodes(node);
        this.draggedNode = node;
    }

    onDragEnd(event, node: PrimeNgTreeNode) {
    }

    onDrop(event, node: PrimeNgTreeNode) {
        if (this.draggedNode) {
            if (this.draggedNode.parent.data.id === node.data.id) {
                return;
            }
            if (this.draggedNode.children && this.draggedNode.children.length !== 0) {
                this.alertService.error('bmsjHipsterApp.tree-table.messages.folder.cannot.move.has.children', { param: this.draggedNode.data.name });
                this.draggedNode = null;
                return;
            } else if (node.leaf) {
                this.alertService.error('bmsjHipsterApp.tree-table.messages.folder.move.not.allowed');
                this.draggedNode = null;
                return;
            }
            this.subscriptionManager.add = this.service.move(this.draggedNode.data.id, node.data.id).subscribe((res) => {
                this.onMoveSuccess(this.draggedNode, res);

                if (!node.children) {
                    node.children = [];
                }
                node.children.push(this.draggedNode);
                this.removeParent(this.draggedNode);
                this.draggedNode.parent = node;
                this.redrawNodes();
                this.draggedNode = null;
            },
            (res: HttpErrorResponse) => {
                this.alertService.error('bmsjHipsterApp.tree-table.messages.error', { param: res.error.errors[0].message });
                this.redrawNodes();
            });
        }
    }

    onMoveSuccess(draggedNode: PrimeNgTreeNode, res: any) {

    }

    onDragEnter(event, node: PrimeNgTreeNode) {
    }

    onDragLeave(event, node: PrimeNgTreeNode) {
    }

    private removeParent(node: PrimeNgTreeNode) {
        if (!node || !node.parent || !node.parent.children) {
            return;
        }
        const indexOf = node.parent.children.indexOf(node, 0);
        if (indexOf > -1) {
            node.parent.children.splice(indexOf, 1);
        }
    }

    private collectExpandedNodes(expandedNodes: string[], node: PrimeNgTreeNode) {
        if (!node || !node.expanded) {
            return;
        }
        expandedNodes.push(node.data.id);
        if (node.children.length > 0) {
            node.children.forEach((c) => this.collectExpandedNodes(expandedNodes, c));
        }
    }

    async persistTreeState() {
        const programExpandedNodes = this.collectTreeExpandedNodes(this.PROGRAM_LIST_FOLDER);
        const cropExpandedNodes = this.collectTreeExpandedNodes(this.CROP_LIST_FOLDER);
        this.subscriptionManager.add = await this.service.persist(cropExpandedNodes, programExpandedNodes).subscribe();
        return Promise.resolve();
    }

    collectTreeExpandedNodes(rootNodeId: string): string[] {
        const expandedNodes = [];
        const rootNode = this.nodes.find((node: PrimeNgTreeNode) => rootNodeId === node.data.id);
        this.collectExpandedNodes(expandedNodes, rootNode);
        if (rootNode && expandedNodes.length === 0) {
            expandedNodes.push(rootNode.data.id);
        }
        return expandedNodes;
    }

    closeModal() {
        const persistPromise = this.persistTreeState();
        persistPromise.then(() => {
            if ((<any>window.parent)) {
                (<any>window.parent).postMessage({ name: TREE_STATE_PERSISTED }, '*');
            }

            this.activeModal.dismiss();
        });
    }

    protected expand(parent) {
        if (parent.leaf) {
            return;
        }
        this.subscriptionManager.add = this.service.expand(parent.data.id)
            .subscribe((res: TreeNode[]) => {
                this.addChildren(parent, res);
                this.redrawNodes();
            });
    }

    addChildren(parent: any, children: TreeNode[]) {
        parent.children = [];
        if (children.length > 0) {
            parent.expanded = true;
            // Recursively add "grand" children nodes as well
            children.forEach((node) => {
                if (this.isFolderSelectionMode && !node.isFolder) {
                    return;
                }
                if (!this.shouldAddNode(node)) {
                    return;
                }
                const child = this.toPrimeNgNode(node, parent);
                parent.children.push(child);
                if (node.children) {
                    this.addChildren(child, node.children);
                }
            });
        }
    }

    redrawNodes() {
        // see primefaces/primeng/issues/5966#issuecomment-402498667
        this.nodes = Object.assign([], this.nodes);
    }

    toPrimeNgNode(node: TreeNode, parent?: PrimeNgTreeNode): PrimeNgTreeNode {
        return {
            label: node.name,
            data: {
                id: node.key,
                name: node.name || '',
                owner: node.owner || '',
                isLocked: node.isLocked || '',
                description: node.description || (parent && '-'), // omit for root folders
                type: node.type || '',
                noOfEntries: node.noOfEntries || ''
            },
            draggable: true,
            droppable: true,
            selectable: this.isSelectable(node),
            leaf: !node.isFolder,
            parent,
        };
    }

    isSelectable(node: TreeNode) {
        return !node.isFolder;
    }

    setMode(mode: Mode, iconClickEvent) {
        if (this.isDisabled(iconClickEvent)) {
            return;
        }

        if (!this.isOneFolderSelected()) {
            this.alertService.error('bmsjHipsterApp.tree-table.messages.folder.select.one.folder');
            return;
        }

        const folder: PrimeNgTreeNode = this.selectedNodes[0];
        this.mode = mode;
        if (this.mode === Mode.Delete) {
            this.validateDeleteFolder(folder);
        } else {
            this.setName(folder);
        }
    }

    private isDisabled(iconClickEvent) {
        return iconClickEvent.target.classList.contains('disable-image');
    }

    private isOneFolderSelected(): boolean {
        return this.selectedNodes.length === 1;
    }

    isAddActionDisabled(): boolean {
        if (!this.isOneFolderSelected()) {
            this.disabledAddActionMessage = this.getFolderSelectionErrorMessage();
            return true;
        }

        const folder: PrimeNgTreeNode = this.selectedNodes[0];

        if (folder.leaf) {
            this.disabledAddActionMessage = this.translateService.instant('bmsjHipsterApp.tree-table.messages.disabled.not.folder.selected');
            return true;
        }

        this.disabledAddActionMessage = '';
        return false;
    }

    isRenameActionDisabled(): boolean {
        if (!this.isOneFolderSelected()) {
            this.disabledRenameActionMessage = this.getFolderSelectionErrorMessage();
            return true;
        }

        const folder: PrimeNgTreeNode = this.selectedNodes[0];
        if (this.isRootFolder(folder)) {
            this.disabledRenameActionMessage = this.translateService.instant('bmsjHipsterApp.tree-table.messages.disabled.root.folder.selected');
            return true;
        }

        if (folder.leaf) {
            this.disabledRenameActionMessage = this.translateService.instant('bmsjHipsterApp.tree-table.messages.disabled.not.folder.selected');
            return true;
        }

        this.disabledRenameActionMessage = '';
        return false;
    }

    isDeleteActionDisabled(): boolean {
        if (!this.isOneFolderSelected()) {
            this.disabledDeleteActionMessage = this.getFolderSelectionErrorMessage();
            return true;
        }

        const folder: PrimeNgTreeNode = this.selectedNodes[0];
        if (this.isRootFolder(folder)) {
            this.disabledDeleteActionMessage = this.translateService.instant('bmsjHipsterApp.tree-table.messages.disabled.root.folder.selected');
            return true;
        }

        if (folder.leaf) {
            this.disabledDeleteActionMessage = this.translateService.instant('bmsjHipsterApp.tree-table.messages.disabled.not.folder.selected');
            return true;
        }

        this.disabledDeleteActionMessage = '';
        return false;
    }

    private getFolderSelectionErrorMessage(): string {
        if (this.selectedNodes.length === 0) {
            return this.translateService.instant('bmsjHipsterApp.tree-table.messages.disabled.no.folder.selected');
        }

        return this.translateService.instant('bmsjHipsterApp.tree-table.messages.disabled.several.items.selected');
    }

    submitAddOrRenameFolder() {
        if (!this.isOneFolderSelected()) {
            return;
        }

        if (this.name.length > this.NAME_MAX_LENGTH) {
            this.alertService.error('bmsjHipsterApp.tree-table.messages.folder.name.too.long', { param: this.NAME_MAX_LENGTH });
            return;
        }

        const folder: PrimeNgTreeNode = this.selectedNodes[0];
        if (this.mode === Mode.Add) {
            this.subscriptionManager.add = this.service.create(this.name, folder.data.id).subscribe((res) => {
                this.mode = this.Modes.None;
                this.expand(folder);
                this.alertService.success('bmsjHipsterApp.tree-table.messages.folder.create.successfully');
            });
        } else if (this.mode === Mode.Rename) {
            this.subscriptionManager.add = this.service.rename(this.name, folder.data.id).subscribe(() => {
                this.mode = this.Modes.None;
                folder.data.name = this.name;
                this.redrawNodes();
                this.alertService.success('bmsjHipsterApp.tree-table.messages.folder.rename.successfully');
            });
        }
    }

    protected validateDeleteFolder(folder: PrimeNgTreeNode) {
        if (folder.children && folder.children.length !== 0) {
            this.alertService.error('bmsjHipsterApp.tree-table.messages.folder.cannot.delete.has.children',
                { param: folder.data.name });
            return;
        }
        this.confirmDeleteFolder(folder);
    }

    private isRootFolder(folder: PrimeNgTreeNode) {
        if (this.rootFolderLabels != null && this.rootFolderLabels.length > 0) {
            return this.rootFolderLabels.includes(folder.label);
        }

        return folder.data.id === this.CROP_LIST_FOLDER || folder.data.id === this.PROGRAM_LIST_FOLDER;
    }

    private confirmDeleteFolder(folder: PrimeNgTreeNode) {
        let message = '';
        if (folder) {
            message = this.translateService.instant('bmsjHipsterApp.tree-table.messages.folder.delete.question',
                { param: folder.data.name });
        } else {
            return;
        }

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = message;
        confirmModalRef.componentInstance.title = this.translateService.instant('bmsjHipsterApp.tree-table.action.folder.delete');

        confirmModalRef.result.then(() => {
            this.submitDeleteFolder(folder);
        }, () => confirmModalRef.dismiss());
    }

    private submitDeleteFolder(folder: PrimeNgTreeNode) {
        this.mode = this.Modes.None;
        this.subscriptionManager.add = this.service.delete(folder.data.id).subscribe(() => {
            this.expand(folder.parent);
            this.alertService.success('bmsjHipsterApp.tree-table.messages.folder.delete.successfully');
        },
        (res: HttpErrorResponse) =>
            this.alertService.error('bmsjHipsterApp.tree-table.messages.error', { param: res.error.errors[0].message })
        );
    }

    private setName(folder: PrimeNgTreeNode) {
        if (this.mode === Mode.Add) {
            this.name = '';
        } else if (this.mode === Mode.Rename) {
            this.name = folder.data.name;
        }
    }

    /**
     * Not a real setter due to union type param
     */
    private setSelectedNodes(selectedNodes: PrimeNgTreeNode[] | PrimeNgTreeNode) {
        if (this.selectionMode === 'multiple') {
            if (Array.isArray(selectedNodes)) {
                this._selectedNodes = selectedNodes;
            } else {
                this._selectedNodes = [selectedNodes];
            }
        } else if (this.selectionMode === 'single' ) {
            if (Array.isArray(selectedNodes)) {
                // programming error
                throw new Error('Set a single element for selectionMode "single"');
            }
            this._selectedNodes = selectedNodes;
        }
    }

    containsForbiddenCharacters(value: string): boolean {
        return !value ? false : this.FORBIDDEN_CHARS.some((char) => value.includes(char));
    }

    shouldAddNode(treeNode: TreeNode): boolean {
        return true;
    }

    translateName(node): string {
        if (node.id === this.CROP_LIST_FOLDER) {
            return this.translateService.instant('global.crop.lists');
        } else if (node.id === this.PROGRAM_LIST_FOLDER) {
            return this.translateService.instant('global.program.lists');
        } else if (node.id === '1' && node.name === 'Studies' && !node.programUUID) {
            return this.translateService.instant('global.studies.folder');
        } else if (node.name === 'Templates' && !node.programUUID && !node.type) {
            return this.translateService.instant('global.templates.folder');
        }
        return node.name;
    }
}

// TODO: move to enum
export enum Mode {
    Add,
    Rename,
    Delete,
    None
}

// TODO reword usages of modal return, making explicit that result is entity agnostic
export interface TreeComponentResult {
    id: number;
    name: string
}
